import React from 'react'
import { type Column } from './utils'
import { TranslationKey } from '@/modules/i18n/types'
import { useTar } from '@/modules'
import { MultiPick } from '@duck/ui'
import { useOrganizations } from '@/storeHooks/useOrganizations'

interface InvitationTableFiltersProps {
  filterText: string
  setFilterText: (text: string) => void
  statusFilter: string[]
  setStatusFilter: (status: string[]) => void
  organizationIdFilter: string[]
  setOrganizationIdFilter: (organizationIds: string[]) => void
  columns: Column[]
  columnDropdownOpen: boolean
  setColumnDropdownOpen: (open: boolean) => void
  toggleColumnVisibility: (columnKey: string) => void
}

const statusOptions: { value: string, label: TranslationKey }[] = [
  { value: 'active', label: 'common.active' },
  { value: 'accepted', label: 'common.accepted' },
  { value: 'expired', label: 'common.expired' },
  { value: 'revoked', label: 'common.revoked' },
]
export const InvitationTableFilters: React.FC<InvitationTableFiltersProps> = ({
  filterText,
  setFilterText,
  statusFilter,
  setStatusFilter,
  organizationIdFilter,
  setOrganizationIdFilter,
  columns,
  columnDropdownOpen,
  setColumnDropdownOpen,
  toggleColumnVisibility,
}) => {
  const { t } = useTar()
  const { organizations } = useOrganizations()

  return (
    <div className="space-y-4">
      {/* Primary Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search Input */}
        <div className="flex-1 min-w-0">
          <label htmlFor="search" className="sr-only">
            {t('invitations.searchInvitations')}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              id="search"
              type="text"
              placeholder={t('common.searchByEmailFirstNameLastName')}
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="block w-full ps-10 pe-3 py-2 border border-border rounded-md leading-5 bg-card placeholder-secondary text-foreground focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent"
            />
          </div>
        </div>

        {/* Status Filter */}
        <div className="w-full sm:w-64">
          <label htmlFor="status-filter" className="sr-only">
            {t('common.filterByStatus')}
          </label>
          <MultiPick
            options={statusOptions.map(o => ({ value: o.value, label: t(o.label) }))}
            selected={statusFilter}
            onChange={(next) => setStatusFilter(next)}
            labels={{ placeholder: t('common.allStatuses') }}
            className="py-[6px]"
          />
        </div>

        {/* Organization Filter */}
        <div className="w-full sm:w-64">
          <label htmlFor="organization-filter" className="sr-only">
            {t('organizations.filterByOrganization')}
          </label>
          <MultiPick
            options={organizations.map(org => ({ value: org.id, label: org.name }))}
            selected={organizationIdFilter}
            onChange={(next) => setOrganizationIdFilter(next)}
            labels={{ placeholder: t('organizations.allOrganizations') }}
            className="py-[6px]"
          />
        </div>

        {/* Column Visibility Dropdown */}
        <div className="w-full sm:w-64">
          <label className="sr-only">
            {t('common.columns')}
          </label>
          <MultiPick
            options={columns.map(col => ({ value: col.key, label: t(col.label) }))}
            selected={columns.filter(c => c.visible).map(c => c.key)}
            onChange={(nextSelected) => {
              const selectedSet = new Set(nextSelected)
              columns.forEach(col => {
                const shouldBeVisible = selectedSet.has(col.key)
                if (shouldBeVisible !== col.visible) {
                  toggleColumnVisibility(col.key)
                }
              })
            }}
            labels={{
              itemsLabel: t('common.items'),
              placeholder: t('common.columns'),
            }}
            className="py-[6px]"
          />
        </div>
      </div>
    </div>
  )
}
